export type {
  FutsalAction,
  StandingPointsBreakdown,
  StandingPointsLineItem,
} from '@/app/[locale]/match/score/components/futsal-types';

import type {
  FutsalAction,
  StandingPointsBreakdown,
  StandingPointsLineItem,
} from '@/app/[locale]/match/score/components/futsal-types';

export function calculateStandingPoints(actions: FutsalAction[]): StandingPointsBreakdown {
  const goalTypes = new Set<FutsalAction['type']>(['goal', 'penalty_goal']);

  const homeGoals = actions.filter((a) => goalTypes.has(a.type) && a.team === 'home').length;
  const awayGoals = actions.filter((a) => goalTypes.has(a.type) && a.team === 'away').length;

  const homeItems: StandingPointsLineItem[] = [];
  const awayItems: StandingPointsLineItem[] = [];

  // Win / Draw / Loss
  if (homeGoals > awayGoals) {
    homeItems.push({
      ruleKey: 'standingRules.win',
      reasonKey: 'standingRules.reasonMostGoals',
      reasonParams: { winner: homeGoals, loser: awayGoals },
      points: 4,
    });
    awayItems.push({
      ruleKey: 'standingRules.loss',
      reasonKey: 'standingRules.reasonMostGoals',
      reasonParams: { winner: homeGoals, loser: awayGoals },
      points: 1,
    });
  } else if (awayGoals > homeGoals) {
    awayItems.push({
      ruleKey: 'standingRules.win',
      reasonKey: 'standingRules.reasonMostGoals',
      reasonParams: { winner: awayGoals, loser: homeGoals },
      points: 4,
    });
    homeItems.push({
      ruleKey: 'standingRules.loss',
      reasonKey: 'standingRules.reasonMostGoals',
      reasonParams: { winner: awayGoals, loser: homeGoals },
      points: 1,
    });
  } else {
    homeItems.push({
      ruleKey: 'standingRules.draw',
      reasonKey: 'standingRules.reasonDrawGoals',
      reasonParams: { goals: homeGoals },
      points: 2,
    });
    awayItems.push({
      ruleKey: 'standingRules.draw',
      reasonKey: 'standingRules.reasonDrawGoals',
      reasonParams: { goals: awayGoals },
      points: 2,
    });
  }

  // Super Cap
  const homeCaptainGoals = actions.filter(
    (a) => goalTypes.has(a.type) && a.team === 'home' && a.isCaptainGoal === true,
  ).length;
  const awayCaptainGoals = actions.filter(
    (a) => goalTypes.has(a.type) && a.team === 'away' && a.isCaptainGoal === true,
  ).length;

  if (homeCaptainGoals > awayCaptainGoals) {
    homeItems.push({
      ruleKey: 'standingRules.superCap',
      reasonKey: 'standingRules.reasonCaptainGoals',
      reasonParams: { winner: homeCaptainGoals, loser: awayCaptainGoals },
      points: 1,
    });
  } else if (awayCaptainGoals > homeCaptainGoals) {
    awayItems.push({
      ruleKey: 'standingRules.superCap',
      reasonKey: 'standingRules.reasonCaptainGoals',
      reasonParams: { winner: awayCaptainGoals, loser: homeCaptainGoals },
      points: 1,
    });
  }

  // Super GK
  const formatTimer = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const homePenaltySaved = actions.find((a) => a.type === 'penalty_saved' && a.team === 'home');
  const awayPenaltySaved = actions.find((a) => a.type === 'penalty_saved' && a.team === 'away');

  if (homePenaltySaved) {
    homeItems.push({
      ruleKey: 'standingRules.superGK',
      reasonKey: 'standingRules.reasonPenaltySaved',
      reasonParams: { time: formatTimer(homePenaltySaved.timerValue) },
      points: 1,
    });
  }
  if (awayPenaltySaved) {
    awayItems.push({
      ruleKey: 'standingRules.superGK',
      reasonKey: 'standingRules.reasonPenaltySaved',
      reasonParams: { time: formatTimer(awayPenaltySaved.timerValue) },
      points: 1,
    });
  }

  // Defensive Bonus
  const isDraw = homeGoals === awayGoals;
  if (!isDraw) {
    const diff = Math.abs(homeGoals - awayGoals);
    if (diff < 3) {
      const item: StandingPointsLineItem = {
        ruleKey: 'standingRules.defensiveBonus',
        reasonKey: 'standingRules.reasonLostBy',
        reasonParams: { diff },
        points: 1,
      };
      if (homeGoals < awayGoals) {
        homeItems.push(item);
      } else {
        awayItems.push(item);
      }
    }
  }

  const homeTotal = homeItems.reduce((sum, item) => sum + item.points, 0);
  const awayTotal = awayItems.reduce((sum, item) => sum + item.points, 0);

  return {
    home: { total: homeTotal, items: homeItems },
    away: { total: awayTotal, items: awayItems },
  };
}
